import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { StaffLoginRequestSchema, type StaffLoginRequest } from '@naya/shared'

import { OwnerApiError } from '../../owner/ownerApi.js'
import { OwnerThemeToggle } from '../../owner/components/OwnerThemeToggle.js'
import { PasswordInput } from '../../shared/PasswordInput.js'
import { NayaWordmark } from '../../shared/brand/NayaWordmark.js'
import { useStaffLogin, useStaffSession } from '../hooks/useStaffSession.js'

/**
 * Staff login — tenant slug + username + password. Separate from owner login
 * because staff are scoped per tenant: the slug resolves the tenant, then
 * (username, password) authenticate within that scope. On success → /staff.
 */
export function StaffLoginPage() {
  const navigate = useNavigate()
  const { data: me, isLoading: meLoading } = useStaffSession()
  const login = useStaffLogin()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<StaffLoginRequest>({
    resolver: zodResolver(StaffLoginRequestSchema),
  })

  if (!meLoading && me) return <Navigate to="/staff" replace />

  function onSubmit(values: StaffLoginRequest) {
    login.mutate(values, {
      onSuccess: () => navigate('/staff', { replace: true }),
      onError: (err) => {
        setError('root', {
          message:
            err instanceof OwnerApiError
              ? err.userMessage
              : 'Something went wrong. Please try again.',
        })
      },
    })
  }

  return (
    <div className="owner-root">
      <div className="owner-auth-screen">
        <div className="owner-auth-card">
          <div className="owner-auth-brand">
            <NayaWordmark className="mark" />
          </div>
          <p className="owner-auth-title">Staff log in</p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {errors.root && (
              <div className="owner-auth-error" role="alert">
                {errors.root.message}
              </div>
            )}

            <div className="owner-field">
              <label className="owner-label" htmlFor="slug">
                Business code
              </label>
              <input
                id="slug"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                className="owner-input"
                placeholder="your-business"
                aria-invalid={errors.slug ? 'true' : undefined}
                {...register('slug')}
              />
              <p className="owner-hint">
                The business slug your owner gave you (not a branch name).
              </p>
              {errors.slug && (
                <p className="owner-field-error">{errors.slug.message}</p>
              )}
            </div>

            <div className="owner-field">
              <label className="owner-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                className="owner-input"
                aria-invalid={errors.email ? 'true' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <p className="owner-field-error">{errors.email.message}</p>
              )}
            </div>

            <div className="owner-field">
              <label className="owner-label" htmlFor="password">
                Password
              </label>
              <PasswordInput
                id="password"
                classPrefix="owner"
                autoComplete="current-password"
                aria-invalid={errors.password ? 'true' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <p className="owner-field-error">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              className="owner-btn owner-btn-primary owner-btn-block"
              disabled={isSubmitting || login.isPending}
            >
              {login.isPending ? 'Logging in…' : 'Log in'}
            </button>
          </form>
          <p
            className="owner-hint"
            style={{ marginTop: 18, textAlign: 'center' }}
          >
            Business owner?{' '}
            <Link to="/login">Sign in to the dashboard</Link>
          </p>
        </div>
        <OwnerThemeToggle />
      </div>
    </div>
  )
}
